import { getFormProductKey } from "@bcc/components/shopify-import/get-form-product-key";
import type { ProductFormStatus } from "@bcc/components/shopify-import/product-form";
import { deriveProductShipTo, imageModifiers, mapWholeBeanVariants, setUploadImage } from "@bcc/components/shopify-import/utils";
import { handleDisplayDraft } from "@bcc/components/shopify-import/utils/handle-display-draft";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bcc/components/ui/dialog";
import type { IntensityComparisonCoffee } from "@bcc/components/ui/intensity-scale";
import { Switch } from "@bcc/components/ui/switch";
import { cn } from "@bcc/lib/utils";
import type { FormProduct, FormVariant, LocalisedProductConfig, ProductConfig, ServiceId } from "@shared/types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProductSelectForm } from "../product-select-form";
import { ConfigureStep } from "./components/configure-step";
import { DialogHeaderContent } from "./components/dialog-header-content/dialog-header-content";
import { ImportErrorAlert } from "./components/import-error-alert/import-error-alert";
import { ModalNavigation } from "./components/modal-navigation/modal-navigation";
import { ReviewSuccess } from "./components/review-success/review-success";
import { StepProgress } from "./components/step-progress/step-progress";
import { setShopifyImportModalTestId } from "./shopify-import-modal.utils";

export interface ShopifyImportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete: (products: FormProduct[]) => Promise<void> | void;
  /**
   * Fired after the success pane has been visible for the full timeout,
   * immediately before the modal is closed. Parents should use this (not
   * onImportComplete) for side effects that trigger background data
   * reloading — firing loading state during the await would unmount the
   * modal before the success pane can be seen.
   */
  onImportFinished?: () => void;
  products: FormProduct[];
  isLoading?: boolean;
  error?: string | null;
  isSingleBagSizeEnabled?: boolean;
  testId?: string;
  // Draft persistence callbacks (parent handles API calls)
  draftProducts?: FormProduct[];
  onDraftChange?: (product: FormProduct) => void;
  onDraftPublished?: (products: FormProduct[]) => void;
  // Resolves `false` (never rejects) when the discard failed server-side —
  // must be checked before optimistically dropping the product from the
  // batch selection (see handleDeleteDraft below).
  onDeleteDraft?: (product: FormProduct) => Promise<boolean>;
  // ProductForm configuration (passed through to ConfigureStep/ProductForm)
  productConfig?: ProductConfig;
  localisedProductConfig?: LocalisedProductConfig;
  shortTermMaxWeeklyInventory?: number;
  shortTermMinWeeklyInventory?: number;
  longTermMinWeeklyInventory?: number;
  longTermMaxWeeklyInventory?: number;
  roasterNoteMaxLength?: number;
  productDescriptionMaxLength?: number;
  titleMaxLength?: number;
  /**
   * The connected vendor's service type (e.g. "shopify", "woocommerce") — used
   * to pick the correct image URL resize modifiers. Not the installation's own
   * id (`Installation.id`); pass `activeInstallation?.serviceId`.
   */
  serviceId?: ServiceId;
  regionLargeBagSize?: string[];
  existingProductTitles?: string[];
  // URL deep-link / refresh restore (§11). All optional + additive so
  // other callers (onboarding) are unaffected.
  /** Step to open at on mount; `configure` restores a deep link. */
  initialStep?: "select" | "configure";
  /** Product ids to pre-select / seed the configure step from on mount. */
  initialSelectedProductIds?: string[];
  /** Emitted (while open) whenever the step or selection changes, so the parent
   *  can mirror it into the URL. */
  onUrlStateChange?: (state: { step: Step; productIds: string[] }) => void;
  marginFloorPercent?: number;
  /**
   * Fired (debounced) when the WooCommerce search input changes, and with
   * `null` when the search is cleared / the modal closes — the parent should
   * treat `null` as "restore the full, unfiltered catalog." Only relevant
   * when `serviceId === "woocommerce"`; unused/no-op for other services.
   */
  onRemoteProductSearch?: (params: { title: string } | { productIds: string } | null) => void;
  /** The roaster's other coffees and their intensity scores, shown as
   *  comparison markers on the intensity scale. Optional — the scale renders
   *  without markers when the list is empty. */
  comparisonCoffees?: readonly IntensityComparisonCoffee[];
}

type Step = "select" | "configure" | "review";
type WooSearchMode = "name" | "id";

const DRAFT_SAVE_TIMEOUT_MS = 1500;
export const WOO_SEARCH_DEBOUNCE_MS = 600;
// Stay on the success pane long enough for the roaster to choose "import more"
// before the fallback auto-close fires (was 3500 for single import).
export const SUCCESS_CLOSE_TIMEOUT_MS = 8000;
const MODAL_RESET_DELAY_MS = 300;
// Fallback for the "import more" loading state if the parent never toggles isLoading.
const IMPORT_MORE_LOADING_MS = 1200;

const normalizeTitle = (title?: string): string => (title ?? "").trim().toLowerCase();

/** Merges any saved draft and auto-loads images (vendor-specific resize modifiers applied via `serviceId`) so the form is pre-filled. */
function prepareProductForConfigure(product: FormProduct, draftProducts: FormProduct[], serviceId: ServiceId | undefined): FormProduct {
  const merged = handleDisplayDraft(product, draftProducts);
  if (!merged.imageUploads?.length && merged.images?.length) {
    const modifiers = imageModifiers(serviceId ?? "", false);
    return { ...merged, imageUploads: merged.images.map((img) => setUploadImage(img.src, modifiers)) };
  }
  return merged;
}

export function ShopifyImportModal({
  open,
  onOpenChange,
  onImportComplete,
  onImportFinished,
  products,
  isLoading = false,
  error: externalError = null,
  isSingleBagSizeEnabled = false,
  testId,
  draftProducts = [],
  onDraftChange,
  onDraftPublished,
  onDeleteDraft,
  productConfig,
  localisedProductConfig,
  shortTermMaxWeeklyInventory,
  shortTermMinWeeklyInventory,
  longTermMinWeeklyInventory,
  longTermMaxWeeklyInventory,
  roasterNoteMaxLength,
  productDescriptionMaxLength,
  titleMaxLength,
  serviceId,
  regionLargeBagSize,
  existingProductTitles,
  initialStep,
  initialSelectedProductIds,
  onUrlStateChange,
  marginFloorPercent,
  onRemoteProductSearch,
  comparisonCoffees,
}: Readonly<ShopifyImportModalProps>) {
  const { t } = useTranslation();
  const isWooCommerce = serviceId === "woocommerce";
  const [step, setStep] = useState<Step>("select");
  const [searchMode, setSearchMode] = useState<WooSearchMode>("name");
  const [wooSearchQuery, setWooSearchQuery] = useState("");
  const wooSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<FormProduct[]>([]);
  // Stable per-product seed used to render the configure forms (identity must
  // not change on keystrokes or the forms would churn). Live values are tracked
  // separately in configuredDataRef.
  const [configureSeed, setConfigureSeed] = useState<FormProduct[]>([]);
  const [formStatuses, setFormStatuses] = useState<Record<string, ProductFormStatus>>({});
  const [productTitles, setProductTitles] = useState<Record<string, string>>({});
  const [importedProducts, setImportedProducts] = useState<Array<{ id: string; name: string }>>([]);
  const [importError, setImportError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [submittedVariantIdsByProduct, setSubmittedVariantIdsByProduct] = useState<Record<string, string[]>>({});

  // Latest collected FormProduct per uuid (ref — no re-render on every keystroke).
  const configuredDataRef = useRef<Record<string, FormProduct>>({});
  // Per-uuid debounced draft-save timers.
  const draftSaveTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Success-pane auto-close fallback timer.
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // "Import more" loading fallback timer.
  const importMoreTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Delayed post-close reset timer (see handleClose).
  const closeResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // URL deep-link restore (§11): becomes true once the initial step has
  // been restored from the URL, gating the URL-emit effect so a refresh doesn't
  // clobber the deep link before it is applied.
  const [urlRestored, setUrlRestored] = useState(false);
  const initStepRef = useRef<Step>("select");
  const initIdsRef = useRef<string[]>([]);
  const prevOpenRef = useRef(false);

  const productsWithDrafts = useMemo(
    () =>
      products
        .map((product) => handleDisplayDraft(product, draftProducts))
        .map((product) => {
          const productId = product.id ?? "";
          const submittedVariantIds = submittedVariantIdsByProduct[productId];
          if (!submittedVariantIds?.length) return product;

          return {
            ...product,
            variants: product.variants.map((variant) =>
              submittedVariantIds.includes(variant.id) ? { ...variant, isImportedRemote: true } : variant
            ),
          };
        }),
    [products, draftProducts, submittedVariantIdsByProduct]
  );

  // Per-product duplicate-name errors: against the existing catalog and against
  // the other in-flight selected titles.
  const titleErrors = useMemo<Record<string, string | undefined>>(() => {
    const existing = new Set((existingProductTitles ?? []).map(normalizeTitle));
    const result: Record<string, string | undefined> = {};
    for (const product of selectedProducts) {
      const uuid = getFormProductKey(product);
      const title = normalizeTitle(productTitles[uuid] ?? product.title);
      if (!title) continue;
      const dupExisting = existing.has(title);
      const dupSibling = selectedProducts.some(
        (other) => getFormProductKey(other) !== uuid && normalizeTitle(productTitles[getFormProductKey(other)] ?? other.title) === title
      );
      if (dupExisting || dupSibling) {
        result[uuid] = t("views.shopifyImportModal.errors.duplicateProductName");
      }
    }
    return result;
  }, [selectedProducts, productTitles, existingProductTitles, t]);

  const canImport = useMemo(
    () =>
      selectedProducts.length > 0 &&
      selectedProducts.every((product) => formStatuses[getFormProductKey(product)] === "valid") &&
      !Object.values(titleErrors).some(Boolean),
    [selectedProducts, formStatuses, titleErrors]
  );

  const clearSuccessTimeout = useCallback(() => {
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
  }, []);

  const clearDraftTimeouts = useCallback(() => {
    draftSaveTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    draftSaveTimeoutsRef.current.clear();
  }, []);

  useEffect(() => {
    if (!open) clearSuccessTimeout();
  }, [open, clearSuccessTimeout]);

  useEffect(
    () => () => {
      clearSuccessTimeout();
      clearDraftTimeouts();
      if (importMoreTimeoutRef.current) clearTimeout(importMoreTimeoutRef.current);
      if (closeResetTimeoutRef.current) clearTimeout(closeResetTimeoutRef.current);
      if (wooSearchTimeoutRef.current) clearTimeout(wooSearchTimeoutRef.current);
    },
    [clearSuccessTimeout, clearDraftTimeouts]
  );

  // Debounced WooCommerce remote search — mirrors the handleDraftSave timeout
  // idiom above. Empty query restores the full catalog (`null`).
  const fireRemoteSearch = useCallback(
    (mode: WooSearchMode, query: string) => {
      if (wooSearchTimeoutRef.current) clearTimeout(wooSearchTimeoutRef.current);
      wooSearchTimeoutRef.current = setTimeout(() => {
        const trimmed = query.trim();
        if (!trimmed) {
          onRemoteProductSearch?.(null);
          return;
        }
        onRemoteProductSearch?.(mode === "id" ? { productIds: trimmed } : { title: trimmed });
      }, WOO_SEARCH_DEBOUNCE_MS);
    },
    [onRemoteProductSearch]
  );

  const handleWooSearchQueryChange = useCallback(
    (value: string) => {
      const nextValue = searchMode === "id" ? value.replace(/\D/g, "") : value;
      setWooSearchQuery(nextValue);
      fireRemoteSearch(searchMode, nextValue);
    },
    [searchMode, fireRemoteSearch]
  );

  const handleSearchModeChange = useCallback(
    (mode: WooSearchMode) => {
      setSearchMode(mode);
      if (wooSearchTimeoutRef.current) clearTimeout(wooSearchTimeoutRef.current);
      if (wooSearchQuery.trim()) onRemoteProductSearch?.(null);
      setWooSearchQuery("");
    },
    [onRemoteProductSearch, wooSearchQuery]
  );

  const handleDraftSave = useCallback(
    (product: FormProduct) => {
      const uuid = getFormProductKey(product);
      const existing = draftSaveTimeoutsRef.current.get(uuid);
      if (existing) clearTimeout(existing);
      const timeout = setTimeout(() => {
        onDraftChange?.({ ...product, hasDraft: false, isProductFormValid: false });
        draftSaveTimeoutsRef.current.delete(uuid);
      }, DRAFT_SAVE_TIMEOUT_MS);
      draftSaveTimeoutsRef.current.set(uuid, timeout);
    },
    [onDraftChange]
  );

  // Collect each form's latest values and persist drafts per product.
  const handleProductChange = useCallback(
    (uuid: string, product: FormProduct) => {
      configuredDataRef.current[uuid] = product;
      handleDraftSave(product);
      setProductTitles((prev) => (prev[uuid] === (product.title ?? "") ? prev : { ...prev, [uuid]: product.title ?? "" }));
    },
    [handleDraftSave]
  );

  const handleProductStatusChange = useCallback((uuid: string, status: ProductFormStatus) => {
    setFormStatuses((prev) => (prev[uuid] === status ? prev : { ...prev, [uuid]: status }));
  }, []);

  // Remove a single product from the batch without leaving Configure, so the
  // roaster keeps every other in-progress form's edits (§13.2).
  const handleProductRemove = useCallback((uuid: string) => {
    delete configuredDataRef.current[uuid];
    const pendingDraftSave = draftSaveTimeoutsRef.current.get(uuid);
    if (pendingDraftSave) {
      clearTimeout(pendingDraftSave);
      draftSaveTimeoutsRef.current.delete(uuid);
    }
    setFormStatuses((prev) => {
      const { [uuid]: _removed, ...rest } = prev;
      return rest;
    });
    setProductTitles((prev) => {
      const { [uuid]: _removed, ...rest } = prev;
      return rest;
    });
    setSelectedProducts((prev) => prev.filter((product) => getFormProductKey(product) !== uuid));
    setConfigureSeed((prev) => prev.filter((product) => getFormProductKey(product) !== uuid));
  }, []);

  // A deleted draft may already be selected; drop it from the batch (via the
  // same cleanup as an explicit "remove from batch") so Continue can't seed
  // Configure from the now-discarded draft data. Only on a *confirmed*
  // discard though — onDeleteDraft resolves false (never rejects) on a
  // failed/skipped backend update, and removing the selection then would
  // lose the batch pick while the draft is still active server-side.
  const handleDeleteDraft = useCallback(
    async (product: FormProduct) => {
      const discarded = await onDeleteDraft?.(product);
      if (discarded) {
        handleProductRemove(getFormProductKey(product));
      }
    },
    [onDeleteDraft, handleProductRemove]
  );

  // Only wired up for products with drafts to delete — resolving via a
  // named variable here (rather than an inline ternary in JSX) keeps
  // ProductSelectForm's props free of prop-position conditionals.
  const resolvedOnDeleteDraft = onDeleteDraft ? handleDeleteDraft : undefined;

  const resetConfigureState = useCallback(() => {
    configuredDataRef.current = {};
    clearDraftTimeouts();
    setConfigureSeed([]);
    setFormStatuses({});
    setProductTitles({});
  }, [clearDraftTimeouts]);

  const resetModalState = useCallback(() => {
    setStep("select");
    setSelectedProducts([]);
    setImportError(null);
    setImportedProducts([]);
    resetConfigureState();
    if (isWooCommerce) {
      setSearchMode("name");
      if (wooSearchTimeoutRef.current) clearTimeout(wooSearchTimeoutRef.current);
      if (wooSearchQuery.trim()) onRemoteProductSearch?.(null);
      setWooSearchQuery("");
    }
  }, [resetConfigureState, isWooCommerce, wooSearchQuery, onRemoteProductSearch]);

  // Seed the configure step from a set of products (draft merge + image
  // autoload), then switch to it. Shared by the Continue button and the URL
  // deep-link restore.
  const seedConfigure = useCallback(
    (toConfigure: FormProduct[]) => {
      const prepared = toConfigure.map((product) => prepareProductForConfigure(product, draftProducts, serviceId));
      configuredDataRef.current = Object.fromEntries(prepared.map((product) => [getFormProductKey(product), product]));
      setSelectedProducts(toConfigure);
      setConfigureSeed(prepared);
      setProductTitles(Object.fromEntries(prepared.map((product) => [getFormProductKey(product), product.title ?? ""])));
      setFormStatuses({});
      setStep("configure");
    },
    [draftProducts, serviceId]
  );

  // Step 1 → Step 2: seed every selected product (draft merge + image autoload).
  const handleContinueToConfiguration = useCallback(() => {
    if (selectedProducts.length === 0) return;
    seedConfigure(selectedProducts);
  }, [selectedProducts, seedConfigure]);

  // Snapshot the requested initial step/ids when the modal transitions to open,
  // so subsequent (reactive) prop changes from our own URL writes can't disturb
  // the restore.
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      // Reset to a clean baseline on every open. Because `open` is URL-driven,
      // the modal can be closed externally (e.g. browser-back stripping
      // `?select=true` / `?import=true`) without `handleClose` running — so a
      // prior session's step/selection could otherwise persist and the modal
      // would reopen on a stale step. The restore effect below re-seeds the
      // configure step from the URL when requested.
      // Cancel a still-pending close reset from `handleClose` so it can't fire
      // after this fresh reset and clobber the newly restored state.
      if (closeResetTimeoutRef.current) {
        clearTimeout(closeResetTimeoutRef.current);
        closeResetTimeoutRef.current = null;
      }
      resetModalState();
      initStepRef.current = initialStep ?? "select";
      initIdsRef.current = initialSelectedProductIds ?? [];

      setUrlRestored(false);
    }
    prevOpenRef.current = open;
  }, [open, initialStep, initialSelectedProductIds, resetModalState]);

  // Restore the configure step from the URL once the product list resolves.
  useEffect(() => {
    if (!open || urlRestored) return;
    const wantsConfigure = initStepRef.current === "configure" && initIdsRef.current.length > 0;
    if (!wantsConfigure) {
      setUrlRestored(true);
      return;
    }
    const resolved = initIdsRef.current
      .map((id) => products.find((product) => product.id === id))
      .filter((product): product is FormProduct => Boolean(product));
    if (resolved.length === 0) {
      if (isLoading) return; // wait for the product list to load before deciding
      // eslint-disable-next-line react-hooks/set-state-in-effect -- no matching products; stay on the select step
      setUrlRestored(true);
      return;
    }
    seedConfigure(resolved);

    setUrlRestored(true);
  }, [open, urlRestored, products, isLoading, seedConfigure]);

  // Mirror the current step + selection into the URL (parent serializes it).
  // Gated on urlRestored so an in-progress refresh restore isn't overwritten.
  useEffect(() => {
    if (open && urlRestored) {
      onUrlStateChange?.({ step, productIds: selectedProducts.map((product) => product.id) });
    }
  }, [open, urlRestored, step, selectedProducts, onUrlStateChange]);

  // Removing the last remaining product (§13.2) leaves nothing to configure —
  // fall back to the Select step rather than rendering an empty Configure step.
  useEffect(() => {
    if (step === "configure" && configureSeed.length === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- batch emptied out; fall back to the select step
      setStep("select");
    }
  }, [step, configureSeed]);

  // Step 2 → Step 1 (back): discard in-memory edits, keep the selection.
  // Clear any import error so the destructive alert doesn't persist on Select.
  const handleBackToSelect = useCallback(() => {
    resetConfigureState();
    setImportError(null);
    setStep("select");
  }, [resetConfigureState]);

  // Modal close handler
  const handleClose = useCallback(() => {
    clearDraftTimeouts();

    // If the success auto-close timer is still pending (user dismissed the
    // review pane early — Escape, outside-click, Exit) promote the parent's
    // post-import side effects now so the table reload isn't skipped.
    if (successTimeoutRef.current) {
      clearSuccessTimeout();
      onImportFinished?.();
    }

    onOpenChange(false);
    if (closeResetTimeoutRef.current) clearTimeout(closeResetTimeoutRef.current);
    closeResetTimeoutRef.current = setTimeout(() => {
      closeResetTimeoutRef.current = null;
      resetModalState();
    }, MODAL_RESET_DELAY_MS);
  }, [onOpenChange, onImportFinished, clearSuccessTimeout, clearDraftTimeouts, resetModalState]);

  // Step 3 → Step 1: import more products. Refresh the parent list and show a
  // loading state until the refreshed products arrive.
  const handleImportMore = useCallback(() => {
    clearSuccessTimeout();
    onImportFinished?.();
    setLoadingMore(true);
    if (importMoreTimeoutRef.current) clearTimeout(importMoreTimeoutRef.current);
    importMoreTimeoutRef.current = setTimeout(() => setLoadingMore(false), IMPORT_MORE_LOADING_MS);
    resetModalState();
  }, [clearSuccessTimeout, onImportFinished, resetModalState]);

  // Step 2 → Step 3: collect every form's values and import as one batch.
  const handleImport = useCallback(async () => {
    if (!canImport) return;
    setImporting(true);
    setImportError(null);

    try {
      const collected = selectedProducts.map((product) => {
        const uuid = getFormProductKey(product);
        const data = configuredDataRef.current[uuid] ?? product;
        // Ship-to is bag-size based: propagate it to same-bag-size
        // grinds, then re-derive the product-level `ship_to` from the whole-bean
        // variant so the Salesforce import mapper (which still reads a single
        // product-level value) stays in sync.
        const mappedVariants = mapWholeBeanVariants(data.variants as FormVariant[]);
        return { ...data, variants: mappedVariants, ship_to: deriveProductShipTo(mappedVariants) ?? data.ship_to } as FormProduct;
      });

      await onImportComplete(collected);
      onDraftPublished?.(collected);

      setSubmittedVariantIdsByProduct((prev) => {
        const next = { ...prev };
        for (const product of collected) {
          const ids = product.variants?.filter((variant) => variant.isProductSelectedForImport).map((variant) => variant.id) ?? [];
          if (product.id && ids.length) {
            next[product.id] = [...new Set([...(next[product.id] ?? []), ...ids])];
          }
        }
        return next;
      });

      setImportedProducts(collected.map((product) => ({ id: getFormProductKey(product), name: product.title ?? product.id })));
      setStep("review");

      clearSuccessTimeout();
      successTimeoutRef.current = globalThis.setTimeout(() => {
        successTimeoutRef.current = null;
        onImportFinished?.();
        handleClose();
      }, SUCCESS_CLOSE_TIMEOUT_MS);
    } catch (err) {
      console.error("Import failed:", err);
      setImportError(t("views.shopifyImportModal.errors.unexpectedError"));
    } finally {
      setImporting(false);
    }
  }, [canImport, selectedProducts, onImportComplete, onDraftPublished, onImportFinished, clearSuccessTimeout, handleClose, t]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent testId={testId ?? setShopifyImportModalTestId("content")} className="sm:max-w-[922px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="sr-only">{t("views.shopifyImportModal.title")}</DialogTitle>
          <DialogDescription className="sr-only">{t(`views.shopifyImportModal.stepDescriptions.${step}`)}</DialogDescription>
          <DialogHeaderContent
            title={t("views.shopifyImportModal.title")}
            description={t(`views.shopifyImportModal.stepDescriptions.${step}`)}
          />
          <div className="flex items-center justify-between pt-4">
            <StepProgress
              currentStep={step}
              steps={[
                { key: "select", label: t("views.shopifyImportModal.steps.select"), number: 1 },
                { key: "configure", label: t("views.shopifyImportModal.steps.configure"), number: 2 },
                { key: "review", label: t("views.shopifyImportModal.steps.review"), number: 3 },
              ]}
            />
            {isWooCommerce && step === "select" && (
              <div className="flex items-center gap-2" data-testid={setShopifyImportModalTestId("search-mode-toggle")}>
                <span className={cn("text-sm font-medium", searchMode === "id" ? "text-muted-foreground" : "text-foreground")}>
                  {t("views.shopifyImportModal.wooSearch.byName")}
                </span>
                <Switch
                  testId={setShopifyImportModalTestId("search-mode-switch")}
                  checked={searchMode === "id"}
                  onCheckedChange={(checked) => handleSearchModeChange(checked ? "id" : "name")}
                  aria-label={t("views.shopifyImportModal.wooSearch.toggleLabel")}
                />
                <span className={cn("text-sm font-medium", searchMode === "id" ? "text-foreground" : "text-muted-foreground")}>
                  {t("views.shopifyImportModal.wooSearch.byId")}
                </span>
              </div>
            )}
          </div>
        </DialogHeader>

        <div className="py-6">
          {/* Step 1: Select Products */}
          {step === "select" && (
            <div className="space-y-4">
              <ProductSelectForm
                products={productsWithDrafts}
                selectedProducts={selectedProducts}
                onSelectionChange={setSelectedProducts}
                onDeleteDraft={resolvedOnDeleteDraft}
                isLoading={isLoading || loadingMore}
                error={externalError}
                isSingleBagSizeEnabled={isSingleBagSizeEnabled}
                searchQuery={isWooCommerce ? wooSearchQuery : undefined}
                onSearchQueryChange={isWooCommerce ? handleWooSearchQueryChange : undefined}
                skipLocalFilter={isWooCommerce && wooSearchQuery.trim().length > 0}
                searchPlaceholder={isWooCommerce ? t(`views.shopifyImportModal.wooSearch.placeholder.${searchMode}`) : undefined}
              />
            </div>
          )}

          {/* Step 2: Configure */}
          {step === "configure" && configureSeed.length > 0 && (
            <ConfigureStep
              products={configureSeed}
              productConfig={productConfig ?? ({} as ProductConfig)}
              localisedProductConfig={localisedProductConfig ?? ({} as LocalisedProductConfig)}
              isSingleBagSizeEnabled={isSingleBagSizeEnabled}
              shortTermMaxWeeklyInventory={shortTermMaxWeeklyInventory}
              shortTermMinWeeklyInventory={shortTermMinWeeklyInventory}
              longTermMinWeeklyInventory={longTermMinWeeklyInventory}
              longTermMaxWeeklyInventory={longTermMaxWeeklyInventory}
              roasterNoteMaxLength={roasterNoteMaxLength}
              productDescriptionMaxLength={productDescriptionMaxLength}
              titleMaxLength={titleMaxLength}
              regionLargeBagSize={regionLargeBagSize}
              marginFloorPercent={marginFloorPercent}
              comparisonCoffees={comparisonCoffees}
              titleErrors={titleErrors}
              onBack={handleBackToSelect}
              onProductChange={handleProductChange}
              onProductStatusChange={handleProductStatusChange}
              onProductRemove={handleProductRemove}
            />
          )}

          {/* Step 3: Review/Success */}
          {step === "review" && (
            <ReviewSuccess
              title={t("views.shopifyImportModal.successBatch.title")}
              description={t("views.shopifyImportModal.successBatch.count", { count: importedProducts.length })}
              redirectingText={t("views.shopifyImportModal.successBatch.redirecting")}
              products={importedProducts}
              onImportMore={handleImportMore}
              importMoreLabel={t("views.shopifyImportModal.successBatch.importMore")}
              onExit={handleClose}
              exitLabel={t("views.shopifyImportModal.successBatch.exit")}
            />
          )}

          <ImportErrorAlert error={importError} />

          <ModalNavigation
            step={step}
            onBack={handleBackToSelect}
            onContinue={handleContinueToConfiguration}
            onImport={handleImport}
            importing={importing}
            canContinue={selectedProducts.length > 0}
            canImport={canImport}
            backLabel={t("buttons.back")}
            importLabel={t("views.shopifyImportModal.importProductsCount", { count: selectedProducts.length })}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
